import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { AnimatePresence, m } from 'motion/react';
import { useEffect, useId, useRef, useState } from 'react';
import type { EstimateSource, MeasurementUnit } from '../api/types';
import { BACKDROP, SHEET } from '../motion/transitions';
import {
  confidenceSteps,
  formatConfidence,
  formatGrams,
  sourceLabel,
  unitLabel,
} from '../format/nutrition';

type ButtonVariant = 'primary' | 'quiet' | 'plain' | 'danger' | 'neutral';

const BUTTON_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn btn-primary',
  quiet: 'btn btn-quiet',
  plain: 'btn btn-plain',
  danger: 'btn btn-danger',
  neutral: 'btn',
};

export function Button({
  variant = 'neutral',
  block = false,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; block?: boolean }) {
  const classes = [BUTTON_CLASS[variant], block ? 'btn-block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return <button type="button" {...rest} className={classes} />;
}

/** Botón cuadrado de barra: días, volver, cerrar. El ícono va como hijo. */
export function IconButton({
  label,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return (
    <button type="button" className="btn-square" aria-label={label} {...rest}>
      {children}
    </button>
  );
}

type CardSize = 'lg' | 'default' | 'tight' | 'flush' | 'dashed';

const CARD_CLASS: Record<CardSize, string> = {
  lg: 'card card-lg',
  default: 'card',
  tight: 'card card-tight',
  flush: 'card card-flush',
  dashed: 'card-dashed',
};

export function Card({ children, size = 'default' }: { children: ReactNode; size?: CardSize }) {
  return <div className={CARD_CLASS[size]}>{children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <span className="eyebrow">{children}</span>;
}

export function Field({
  label,
  hint,
  error,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const id = useId();

  // El error se ataba sólo visualmente: `aria-describedby` apuntaba nada más que a la ayuda, así
  // que quien usa un lector de pantalla veía el campo marcado como inválido sin llegar a escuchar
  // por qué. Se anuncian los dos, y en ese orden.
  const describedBy = [hint !== undefined ? `${id}-hint` : null, error !== undefined ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        aria-invalid={error !== undefined || undefined}
        {...rest}
      />
      {hint !== undefined && (
        <span id={`${id}-hint`} className="micro muted">
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span id={`${id}-error`} className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Un campo de contraseña que se puede mirar.
 *
 * Es la diferencia entre poder usar la aplicación en un teléfono y no poder: RN-17 pide doce
 * caracteres y sugiere una frase entera, y escribir una frase a ciegas en un teclado táctil termina
 * casi siempre en «credenciales incorrectas» sin que la persona sepa qué escribió mal. El ojo no es
 * un adorno, es lo que vuelve viable el requisito de largo.
 */
export function PasswordField({
  label,
  hint,
  error,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  hint?: string;
  error?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="field-with-action">
      <Field {...rest} label={label} hint={hint} error={error} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="field-action"
        // El estado se nombra por lo que hay ahora, no por lo que hará el botón: un lector de
        // pantalla anuncia la etiqueta al enfocar, y «mostrar» sobre una contraseña ya visible
        // dice exactamente lo contrario de la verdad.
        aria-label={visible ? 'Contraseña visible. Ocultarla' : 'Contraseña oculta. Mostrarla'}
        aria-pressed={visible}
        onClick={() => setVisible((current) => !current)}
      >
        {visible ? 'Ocultar' : 'Mostrar'}
      </button>
    </div>
  );
}

export function SelectField({
  label,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  const id = useId();

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} {...rest}>
        {children}
      </select>
    </div>
  );
}

const UNITS: MeasurementUnit[] = ['Gram', 'Milliliter', 'Unit', 'Serving'];

/**
 * Cantidad y unidad juntas: la unidad no es un desplegable sino un segmentado de cuatro, porque
 * son exactamente las cuatro de RF-09 y elegir entre cuatro cosas visibles es más rápido que
 * abrir un menú para ver que había cuatro.
 */
export function NumberFieldWithUnit({
  label,
  value,
  unit,
  onValueChange,
  onUnitChange,
  hint,
}: {
  label: string;
  value: string;
  unit: MeasurementUnit | '';
  onValueChange: (value: string) => void;
  onUnitChange: (unit: MeasurementUnit | '') => void;
  hint?: string;
}) {
  const id = useId();

  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
        />
      </div>
      <div className="segmented" role="group" aria-label="Unidad">
        {UNITS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={unit === option}
            onClick={() => onUnitChange(unit === option ? '' : option)}
          >
            {unitLabel(option)}
          </button>
        ))}
      </div>
      {hint !== undefined && <span className="micro muted">{hint}</span>}
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'decision' | 'danger' | 'conf';
  children: ReactNode;
}) {
  return (
    <p
      className={`banner banner-${tone}`}
      /*
       * Sólo el error interrumpe. `role="alert"` corta lo que el lector de pantalla esté diciendo,
       * y un aviso que espera una decisión —piso calórico, ítem sin confirmar— no es una urgencia:
       * se anuncia con `status`, cuando la frase en curso termina.
       */
      role={tone === 'danger' ? 'alert' : tone === 'decision' ? 'status' : undefined}
    >
      {children}
    </p>
  );
}

export function Badge({
  tone,
  children,
}: {
  tone: 'done' | 'attention' | 'neutral' | 'muted';
  children: ReactNode;
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Chip({
  large = false,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { large?: boolean; children: ReactNode }) {
  return (
    <button type="button" className={large ? 'chip chip-lg' : 'chip'} {...rest}>
      {children}
    </button>
  );
}

export function Spinner({ label = 'Cargando' }: { label?: string }) {
  return (
    <span className="row">
      <span className="spinner" aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon !== undefined && <span className="empty-icon">{icon}</span>}
      <h2>{title}</h2>
      {children}
    </div>
  );
}

/**
 * El indicador de confianza · variante A.
 *
 * Cuatro barras de altura creciente. Es una escala propia, deliberadamente distinta del acento y
 * de los colores de estado: la confianza no es una alarma ni una acción, es una propiedad del
 * dato. En `detail` se envuelve con la fuente y la confianza formateada, porque ahí hay lugar
 * para decir de dónde salió el número además de cuánto vale.
 */
export function ConfidenceMeter({
  source,
  confidence,
  size = 'row',
}: {
  source: EstimateSource;
  confidence: number;
  size?: 'row' | 'detail';
}) {
  const steps = confidenceSteps(confidence);
  const description = `Confianza ${formatConfidence(confidence)} · ${sourceLabel(source)}`;

  const bars = (
    <span className="conf-meter" role="img" aria-label={description}>
      {([1, 2, 3, 4] as const).map((step) => (
        <span
          key={step}
          className={`conf-tick conf-tick-${step}${step <= steps ? ' conf-tick-on' : ''}`}
        />
      ))}
    </span>
  );

  if (size === 'row') {
    return bars;
  }

  return (
    <span className="conf-pill">
      {bars}
      {sourceLabel(source)} · {formatConfidence(confidence)}
    </span>
  );
}

/** Los tres macros con su color fijo. El mismo bloque en el día, en la comida y en el objetivo. */
export function MacroGrid({
  proteinG,
  carbohydratesG,
  fatG,
  target,
  estimated = false,
}: {
  proteinG: number;
  carbohydratesG: number;
  fatG: number;
  target?: { proteinG: number; carbohydratesG: number; fatG: number };
  /**
   * Verdadero cuando el agregado contiene ítems inciertos y no confirmados.
   *
   * La API no da rangos por macro —sólo de kcal— así que acá no se inventa una banda: se marca
   * con «≈» que el número está afectado por la misma estimación que volvió rango al total.
   * Mostrar las kcal como rango y los gramos como exactos era aplicar la honestidad a una
   * columna sí y a la de al lado no.
   */
  estimated?: boolean;
}) {
  const rows = [
    ['protein', 'Proteína', proteinG, target?.proteinG],
    ['carbs', 'Carbohidratos', carbohydratesG, target?.carbohydratesG],
    ['fat', 'Grasa', fatG, target?.fatG],
  ] as const;

  return (
    <div className="macro-grid">
      {rows.map(([key, name, value, goal]) => (
        <div key={key} className={`macro macro-${key}`}>
          <span className="macro-rule" />
          <span className="macro-name">{name}</span>
          <span className="macro-value">
            {estimated ? '≈ ' : ''}
            {formatGrams(value)} g
          </span>
          {goal !== undefined && <span className="macro-target">de {formatGrams(goal)} g</span>}
        </div>
      ))}
    </div>
  );
}

/**
 * La barra de avance del día, en dos tramos.
 *
 * El tramo cierto va en el acento y el incierto rayado con la escala de confianza: así se ve de
 * un vistazo cuánto de lo consumido es un dato y cuánto es una estimación. Se detiene en el
 * 100 % a propósito — pasarse del objetivo no dispara alarma ni color de peligro, sólo deja de
 * llenarse.
 */
export function Progress({
  certain,
  uncertain,
  max,
  label,
}: {
  certain: number;
  uncertain?: number;
  max: number;
  label: string;
}) {
  const scale = (value: number) => (max > 0 ? Math.min(100, (value / max) * 100) : 0);
  const certainWidth = scale(certain);
  const uncertainWidth = Math.max(0, scale(certain + (uncertain ?? 0)) - certainWidth);

  return (
    <div
      className="progress"
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(certainWidth + uncertainWidth)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span className="progress-certain" style={{ width: `${certainWidth}%` }} />
      {uncertainWidth > 0 && (
        <span className="progress-uncertain" style={{ width: `${uncertainWidth}%` }} />
      )}
    </div>
  );
}

/** Hoja inferior modal. Cierra con Escape y devuelve el foco: es el patrón de toda edición. */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const headingId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);

  /*
   * El panel maneja su propia salida.
   *
   * Con CSS la entrada se animaba y el cierre era un corte seco: el elemento se desmonta y no hay
   * nada que animar. `AnimatePresence` lo retiene mientras se va, pero necesita que la condición
   * viva adentro — por eso el estado es local y `onClose` se avisa recién cuando la animación
   * terminó. Para quien llama, la interfaz no cambió: sigue montando el panel y recibiendo
   * `onClose`.
   */
  const [open, setOpen] = useState(true);
  const dismiss = () => setOpen(false);

  useEffect(() => {
    const previous = document.activeElement;
    sheetRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) {
        previous.focus();
      }
    };
  }, []);

  return (
    <AnimatePresence onExitComplete={onClose}>
      {open && (
        <m.div
          className="sheet-backdrop"
          variants={BACKDROP}
          initial="hidden"
          animate="visible"
          exit="gone"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              dismiss();
            }
          }}
        >
          <m.div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={headingId}
            tabIndex={-1}
            ref={sheetRef}
            variants={SHEET}
          >
            <div className="row-between">
              <h2 id={headingId} style={{ fontSize: '1.125rem' }}>
                {title}
              </h2>
              <Button variant="plain" onClick={dismiss} aria-label="Cerrar">
                Cerrar
              </Button>
            </div>
            {children}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

/** Elección entre pocas opciones excluyentes, sin abrir un menú. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * El teclado numérico propio de la declaración.
 *
 * El teclado del sistema tapa media pantalla y cambia de alto entre dispositivos, lo que mueve el
 * botón primario justo cuando la persona va a tocarlo. Este ocupa un alto conocido y deja el
 * primario donde estaba.
 */
export function Keypad({
  onDigit,
  onDecimal,
  onBackspace,
}: {
  onDigit: (digit: string) => void;
  onDecimal: () => void;
  onBackspace: () => void;
}) {
  return (
    <div className="keypad" role="group" aria-label="Teclado numérico">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
        <button key={digit} type="button" onClick={() => onDigit(digit)}>
          {digit}
        </button>
      ))}
      <button type="button" onClick={onDecimal} aria-label="Coma decimal">
        ,
      </button>
      <button type="button" onClick={() => onDigit('0')}>
        0
      </button>
      <button type="button" onClick={onBackspace} aria-label="Borrar">
        ⌫
      </button>
    </div>
  );
}
